import React, { useRef, useState } from 'react'
import { IconButton, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import GroupIcon from '@mui/icons-material/Group'
import EventIcon from '@mui/icons-material/Event'
import ListIcon from '@mui/icons-material/List'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import NoteIcon from '@mui/icons-material/Note'
import DashboardIcon from '@mui/icons-material/Dashboard'
import { Package } from '../../../enums/package.enum'
import { isMinimumPackage } from '../../../utils/package-validator'
import { useSelector } from 'react-redux'
import { State } from '../../../types/state'
import FileDownloadIcon from '@mui/icons-material/FileDownload'

interface ProjectDetailsIconsProps {
  activeTab: string
  onTabChange: (tab: string) => void
  project: any
  onShareProject: () => void
  onUnshareProject: () => void
  onOpenShareModal: () => void
  onExportToExcel: () => void
  onExportToPdf: () => void
  onExportToMoodboard: () => void
  onExportToTasks: () => void
}

const ProjectDetailsIcons: React.FC<ProjectDetailsIconsProps> = ({
  activeTab,
  onTabChange,
  project,
  onShareProject,
  onUnshareProject,
  onOpenShareModal,
  onExportToExcel,
  onExportToPdf,
  onExportToMoodboard,
  onExportToTasks
}) => {
  const { package: userPackage, packageExpiredAt } = useSelector((state: State) => state.user)

  const tabIcons = [
    ...(isMinimumPackage(userPackage, Package.PREMIUM, packageExpiredAt) ? [{ label: 'Szczegóły', value: 'details', icon: <InfoIcon /> }] : []),
    ...(isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) ? [{ label: 'Zespół', value: 'team', icon: <GroupIcon /> }] : []),
    ...(isMinimumPackage(userPackage, Package.PREMIUM, packageExpiredAt) ? [{ label: 'Harmonogram', value: 'schedule', icon: <EventIcon /> }] : []),
    { label: 'Lista produktów', value: 'products', icon: <ListIcon /> },
    ...(isMinimumPackage(userPackage, Package.PREMIUM, packageExpiredAt) ? [{ label: 'Załączniki', value: 'attachments', icon: <AttachFileIcon /> }] : []),
    { label: 'Notatki', value: 'notes', icon: <NoteIcon /> },
    ...(isMinimumPackage(userPackage, Package.PREMIUM, packageExpiredAt) ? [{ label: 'Powiązane tablice', value: 'linked-tasks', icon: <DashboardIcon /> }] : [])
  ]

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)
  const exportDropdownRef = useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!exportDropdownOpen) return
    const handleClick = (e: MouseEvent) => {
      if ((exportDropdownRef.current != null) && !exportDropdownRef.current.contains(e.target as Node)) {
        setExportDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [exportDropdownOpen])

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: window.innerWidth <= 600 ? 'wrap' : 'nowrap',
        justifyContent: 'center',
        gap: 12,
        maxWidth: window.innerWidth <= 600 ? '320px' : 'none',
        width: window.innerWidth <= 600 ? '100%' : 'auto'
      }}
    >
      {tabIcons.map(tab => (
        <Tooltip title={tab.label} key={tab.value}>
          <IconButton
            color={activeTab === tab.value ? 'primary' : 'default'}
            onClick={() => onTabChange(tab.value)}
            sx={{
              transition: 'all 0.2s ease-in-out',
              borderRadius: '8px',
              padding: '8px',
              position: 'relative',
              flex: { xs: '0 0 25%', sm: 'unset' },
              maxWidth: { xs: '25%', sm: 'unset' },
              '@media (max-width: 600px)': {
                padding: '4px',
                flex: '0 0 25%',
                maxWidth: '25%',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem'
                }
              },
              '&::after': activeTab === tab.value
                ? {
                    content: '""',
                    position: 'absolute',
                    bottom: '-2px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '20px',
                    height: '3px',
                    backgroundColor: '#47a145',
                    borderRadius: '2px'
                  }
                : {},
              '& .MuiSvgIcon-root': {
                color: '#47a145',
                transition: 'color 0.2s ease-in-out'
              }
            }}
          >
            {tab.icon}
          </IconButton>
        </Tooltip>
      ))}
      {/* Udostępnianie */}
      {project?.isShared && isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && (
        <Tooltip title='Udostępnij' key='share'>
          <IconButton
            color='primary'
            onClick={onOpenShareModal}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              },
              '@media (max-width: 600px)': {
                padding: '4px',
                flex: '0 0 25%',
                maxWidth: '25%',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem'
                }
              },
              '& .fa': {
                color: '#47a145'
              }
            }}
          >
            <i className='fa fa-share-alt' />
          </IconButton>
        </Tooltip>
      )}
      {project?.isShared && isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && (
        <Tooltip title='Wyłącz udostępnianie' key='unshare'>
          <IconButton
            color='primary'
            onClick={onUnshareProject}
            sx={{
              '@media (max-width: 600px)': {
                padding: '4px',
                flex: '0 0 25%',
                maxWidth: '25%',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem'
                }
              },
              '& .fa': {
                color: '#47a145'
              }
            }}
          >
            <i className='fa fa-times' />
          </IconButton>
        </Tooltip>
      )}
      {!project?.isShared && isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && (
        <Tooltip title='Udostępnij' key='share-new'>
          <IconButton
            color='primary'
            onClick={onShareProject}
            sx={{
              '@media (max-width: 600px)': {
                padding: '4px',
                flex: '0 0 25%',
                maxWidth: '25%',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem'
                }
              },
              '& .fa': {
                color: '#47a145'
              }
            }}
          >
            <i className='fa fa-share-alt' />
          </IconButton>
        </Tooltip>
      )}
      <div className='tw-relative' ref={exportDropdownRef}>
        <Tooltip title='Eksportuj' key='export'>
          <IconButton
            color='primary'
            onClick={() => setExportDropdownOpen((v) => !v)}
            sx={{
              '@media (max-width: 600px)': {
                padding: '4px',
                flex: '0 0 25%',
                maxWidth: '25%',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem'
                }
              },
              '& .MuiSvgIcon-root': {
                color: '#47a145'
              }
            }}
          >
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>
        {exportDropdownOpen && (
          <div className='tw-absolute tw-right-0 tw-mt-2 tw-w-56 tw-bg-white tw-rounded-md tw-shadow-lg tw-z-50 tw-border tw-border-slate-200'>
            {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && <button
              className='tw-flex tw-items-center tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-left hover:tw-bg-slate-100 tw-transition'
              onClick={() => { setExportDropdownOpen(false); onExportToExcel() }}
            >
              <i className='fa fa-file-excel tw-mr-2' /> Eksport do Excel
            </button>}
            <button
              className='tw-flex tw-items-center tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-left hover:tw-bg-slate-100 tw-transition'
              onClick={() => { setExportDropdownOpen(false); onExportToPdf() }}
            >
              <i className='fa fa-file-pdf tw-mr-2' /> Eksport do PDF
            </button>
            {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && <button
              className='tw-flex tw-items-center tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-left hover:tw-bg-slate-100 tw-transition'
              onClick={() => { setExportDropdownOpen(false); onExportToMoodboard() }}
            >
              <i className='fa fa-image tw-mr-2' /> Eksport do moodboard
            </button>}
          </div>
        )}
      </div>
      {/* Tablica zadań */}
      {isMinimumPackage(userPackage, Package.PREMIUM, packageExpiredAt) && (
        <Tooltip title='Utwórz tablicę zadań' key='tasks'>
          <IconButton
            color='primary'
            onClick={onExportToTasks}
            sx={{
              '@media (max-width: 600px)': {
                padding: '4px',
                flex: '0 0 25%',
                maxWidth: '25%',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem'
                }
              },
              '& .fa': {
                color: '#47a145'
              }
            }}
          >
            <i className='fa fa-tasks' />
          </IconButton>
        </Tooltip>
      )}
    </div>
  )
}

export default ProjectDetailsIcons
